/**
 * Tự động hoá — danh sách luồng dạng thẻ: tìm kiếm, lọc theo nhóm kích hoạt /
 * trạng thái, bật/tắt, mở trình dựng, nhật ký, nhân bản, xoá; tạo luồng trống
 * hoặc từ thư viện mẫu.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, Bot, Copy, History, Pencil, Plus, Search, Sparkles, Trash2, Workflow } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { i18n, currentLocale } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  emptyFlowConfig, useAutomationRules, useCreateRule, useDeleteRule, useDuplicateRule, useUpdateRule, type AutomationRule,
} from '@/hooks/use-automation'
import { NHOM_TRIGGER, triggerMeta, type NhomTrigger } from '@/lib/automation/catalog'
import type { MauWorkflow } from '@/lib/automation/mau-workflow'
import { ChonKichHoatDialog, ChonMauDialog, VongIcon } from './khoi-canvas'
import { NhatKyDialog } from './nhat-ky-hoat-dong'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { AiComposerPanel } from './ai-composer-panel'

const TRANG_THAI_LUOT: Record<NonNullable<AutomationRule['lastRunStatus']>, { ten: string; variant: 'success' | 'warning' | 'destructive' | 'default' }> = {
  get ok() { return { ten: i18n.t('autoTemplates:list.runStatus.ok'), variant: 'success' as const } },
  get partial() { return { ten: i18n.t('autoTemplates:list.runStatus.partial'), variant: 'warning' as const } },
  get error() { return { ten: i18n.t('autoTemplates:list.runStatus.error'), variant: 'destructive' as const } },
  get waiting() { return { ten: i18n.t('autoTemplates:list.runStatus.waiting'), variant: 'default' as const } },
}

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(currentLocale(), { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : null)

export function AutomationPage() {
  const { t } = useTranslation('autoTemplates')
  const nav = useNavigate()
  // Quyền theo vai trò động (automation-routes): tạo / nhân bản / tạo bằng AI / từ mẫu = `automation.create`,
  // bật-tắt = `automation.update`, xoá = `automation.delete`. Không có quyền → không hiện nút (đang tải = chưa có quyền).
  const { data: perms } = useMyPermissions()
  const coTao = !!perms?.has('automation.create')
  const coSua = !!perms?.has('automation.update')
  const coXoa = !!perms?.has('automation.delete')
  const { data, isLoading, isError } = useAutomationRules()
  const update = useUpdateRule()
  const duplicate = useDuplicateRule()
  const del = useDeleteRule()
  const create = useCreateRule()

  const [q, setQ] = useState('')
  const [nhom, setNhom] = useState<NhomTrigger | 'all'>('all')
  const [trangThai, setTrangThai] = useState<'all' | 'on' | 'off'>('all')
  const [moTao, setMoTao] = useState(false)
  const [moAi, setMoAi] = useState(false)
  const [moMau, setMoMau] = useState(false)
  const [moNhatKy, setMoNhatKy] = useState(false)
  const [xoa, setXoa] = useState<AutomationRule | null>(null)

  const rules = useMemo(() => data?.rules ?? [], [data])
  const hien = useMemo(() => rules.filter((r) => {
    if (trangThai === 'on' && !r.enabled) return false
    if (trangThai === 'off' && r.enabled) return false
    if (nhom !== 'all' && triggerMeta(r.trigger).nhom !== nhom) return false
    if (q && !`${r.name} ${r.description ?? ''} ${triggerMeta(r.trigger).ten}`.toLowerCase().includes(q.toLowerCase())) return false
    return true
  }), [rules, q, nhom, trangThai])

  const batTat = (r: AutomationRule, v: boolean) => update.mutate({ id: r.id, data: { enabled: v } }, {
    onSuccess: () => toast.success(v ? t('list.toast.enabled', { name: r.name }) : t('list.toast.disabled', { name: r.name })),
    onError: (e) => toast.error(apiError(e)),
  })
  const nhanBan = (r: AutomationRule) => duplicate.mutate(r, {
    onSuccess: (moi) => toast.success(t('list.toast.duplicated', { name: moi.name })),
    onError: (e) => toast.error(apiError(e)),
  })
  // Mở từ trang Bắt đầu (bước 4): tạo xong → sang trình dựng luồng, giữ from/step + báo xong bước (không tự kéo về).
  const onboardingReturn = useOnboardingReturn()
  const moTrinhDung = (id: string) => {
    onboardingReturn.complete(4, { autoReturn: false })
    nav(onboardingReturn.carry(`/automation/flow/${id}`, 4))
  }
  const taoTuMau = (mau: MauWorkflow) => create.mutate(
    { name: mau.tenLuu, description: mau.moTaLuu, trigger: mau.trigger, enabled: false, flowVersion: 2, flowConfig: structuredClone(mau.flow) },
    { onSuccess: (r) => moTrinhDung(r.id), onError: (e) => toast.error(apiError(e)) },
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={(
          <>
            <Button variant="outline" onClick={() => setMoNhatKy(true)}><History className="h-4 w-4" /> {t('list.logs')}</Button>
            {coTao && <Button onClick={() => setMoTao(true)}><Plus className="h-4 w-4" /> {t('list.createFlow')}</Button>}
          </>
        )}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('list.searchPlaceholder')} className="pl-8" />
        </div>
        <Select value={nhom} onValueChange={(v) => setNhom(v as NhomTrigger | 'all')}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.allTriggers')}</SelectItem>
            {(Object.keys(NHOM_TRIGGER) as NhomTrigger[]).map((k) => <SelectItem key={k} value={k}>{NHOM_TRIGGER[k].ten}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={trangThai} onValueChange={(v) => setTrangThai(v as 'all' | 'on' | 'off')}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t('list.state.all')}</SelectItem><SelectItem value="on">{t('list.state.on')}</SelectItem><SelectItem value="off">{t('list.state.off')}</SelectItem></SelectContent>
        </Select>
        {rules.length > 0 && <span className="ml-auto text-xs text-muted-foreground">{t('list.summary', { shown: hien.length, total: rules.length, on: rules.filter((r) => r.enabled).length })}</span>}
      </div>

      {isLoading ? <Loading label={t('list.loading')} />
        : isError ? <ErrorState message={t('list.loadError')} />
        : rules.length === 0 ? (
          <EmptyState icon={Workflow} title={t('list.empty.title')}
            description={t('list.empty.description')}
            action={coTao && <div className="flex gap-2"><Button variant="outline" onClick={() => setMoAi(true)}><Bot className="h-4 w-4" /> {t('list.createWithAi')}</Button><Button variant="outline" onClick={() => setMoMau(true)}><Sparkles className="h-4 w-4" /> {t('list.chooseTemplate')}</Button><Button onClick={() => setMoTao(true)}><Plus className="h-4 w-4" /> {t('list.createFlow')}</Button></div>} />
        ) : hien.length === 0 ? <EmptyState icon={Search} title={t('list.noMatch')} />
        : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {hien.map((r) => <TheLuong key={r.id} r={r} coSua={coSua} coNhanBan={coTao} coXoa={coXoa} dangDoi={update.isPending && update.variables?.id === r.id}
              onBatTat={(v) => batTat(r, v)} onMo={(nk) => nav(`/automation/flow/${r.id}${nk ? '?nhatky=1' : ''}`)}
              onNhanBan={() => nhanBan(r)} onXoa={() => setXoa(r)} />)}
          </div>
        )}

      {moTao && <TaoLuongDialog onClose={() => setMoTao(false)} onChonMau={() => { setMoTao(false); setMoMau(true) }} onAi={() => { setMoTao(false); setMoAi(true) }} />}
      {moAi && <TaoBangAiDialog onClose={() => setMoAi(false)} />}
      <ChonMauDialog open={moMau} onClose={() => setMoMau(false)} onChon={taoTuMau} canhBao={t('list.templateWarning')} />
      <NhatKyDialog open={moNhatKy} onClose={() => setMoNhatKy(false)} />

      <Dialog open={!!xoa} onOpenChange={(v) => !v && setXoa(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('list.delete.title')}</DialogTitle><DialogDescription>{t('list.delete.description', { name: xoa?.name })}</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setXoa(null)}>{t('common:actions.cancel')}</Button>
            <Button variant="destructive" disabled={del.isPending} onClick={() => xoa && del.mutate(xoa.id, {
              onSuccess: () => { toast.success(t('list.toast.deleted')); setXoa(null) },
              onError: (e) => toast.error(apiError(e)),
            })}>{t('common:actions.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function TheLuong({ r, coSua, coNhanBan, coXoa, dangDoi, onBatTat, onMo, onNhanBan, onXoa }: {
  r: AutomationRule; coSua: boolean; coNhanBan: boolean; coXoa: boolean; dangDoi: boolean
  onBatTat: (v: boolean) => void; onMo: (nhatKy?: boolean) => void; onNhanBan: () => void; onXoa: () => void
}) {
  const { t } = useTranslation('autoTemplates')
  const tm = triggerMeta(r.trigger)
  const soKhoi = r.flowConfig?.nodes?.length ?? 0
  const chuaCoLuong = !r.flowConfig?.trigger
  const lanCuoi = fmt(r.lastRunAt)
  return (
    <Card className={cn('flex flex-col', !r.enabled && 'opacity-80')}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <button type="button" className="min-w-0 text-left" onClick={() => onMo()}><CardTitle className="text-base leading-snug hover:underline">{r.name}</CardTitle></button>
          {coSua
            ? <Switch checked={r.enabled} disabled={dangDoi} onCheckedChange={onBatTat} title={r.enabled ? t('list.state.on') : t('list.state.off')} />
            : <Badge variant={r.enabled ? 'success' : 'outline'} className="shrink-0 font-normal">{r.enabled ? t('list.state.on') : t('list.state.off')}</Badge>}
        </div>
        <CardDescription className="line-clamp-2">{r.description || tm.moTa}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-2 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="outline" className="gap-1"><VongIcon icon={tm.icon} mau={tm.mau} size="h-4 w-4" /> {tm.ten}</Badge>
          <Badge variant="secondary">{t('list.card.blocks', { count: soKhoi })}</Badge>
          {r.runCount > 0 && <Badge variant="secondary">{t('list.card.runCount', { count: r.runCount, formatted: formatNumber(r.runCount) })}</Badge>}
          {r.priority !== 0 && <Badge variant="outline" title={t('list.card.priorityHint')}>{t('list.card.priority', { value: r.priority })}</Badge>}
        </div>
        {lanCuoi && (
          <div className="flex flex-wrap items-center gap-1.5">{t('list.card.lastRun', { time: lanCuoi })}
            {r.lastRunStatus && <Badge variant={TRANG_THAI_LUOT[r.lastRunStatus].variant} className="px-1.5 py-0 text-[10px]">{TRANG_THAI_LUOT[r.lastRunStatus].ten}</Badge>}
          </div>
        )}
        {chuaCoLuong && <p className="flex items-start gap-1 text-warning"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {t('list.card.legacy')}</p>}
        {!chuaCoLuong && tm.khongCoNguonPhat && <p className="flex items-start gap-1 text-warning"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {t('list.card.triggerGone', { name: tm.ten })}</p>}
        <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
          <Button size="sm" onClick={() => onMo()}><Pencil className="h-3.5 w-3.5" /> {coSua ? t('list.card.edit') : t('common:actions.view')}</Button>
          <Button size="sm" variant="outline" onClick={() => onMo(true)}><History className="h-3.5 w-3.5" /> {t('list.logs')}</Button>
          {coNhanBan && <Button size="sm" variant="outline" onClick={onNhanBan}><Copy className="h-3.5 w-3.5" /> {t('common:actions.duplicate')}</Button>}
          {coXoa && <Button size="sm" variant="ghost" className="text-destructive" onClick={onXoa} title={t('list.delete.tooltip')}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </CardContent>
    </Card>
  )
}

/** Tạo luồng trống: đặt tên + chọn kích hoạt → mở trình dựng. */
function TaoBangAiDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('autoTemplates')
  const nav = useNavigate()
  const create = useCreateRule()
  const tao = (kq: import('@/hooks/use-automation-ai').ComposeResult) => create.mutate(
    { name: (kq.name || t('aiDialog.defaultName')).trim(), trigger: kq.flowConfig.trigger.type, enabled: false, flowVersion: 2, flowConfig: kq.flowConfig },
    {
      onSuccess: (r) => { toast.success(t('aiDialog.created')); nav(`/automation/flow/${r.id}`) },
      onError: (e) => toast.error(apiError(e)),
    },
  )
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-xl flex-col">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Bot className="h-4 w-4" /> {t('aiDialog.title')}</DialogTitle><DialogDescription>{t('aiDialog.description')}</DialogDescription></DialogHeader>
        <AiComposerPanel className="min-h-0 flex-1" applyLabel={t('aiDialog.apply')} applying={create.isPending} onApply={tao} />
      </DialogContent>
    </Dialog>
  )
}

function TaoLuongDialog({ onClose, onChonMau, onAi }: { onClose: () => void; onChonMau: () => void; onAi: () => void }) {
  const { t } = useTranslation('autoTemplates')
  const nav = useNavigate()
  const onboardingReturn = useOnboardingReturn()
  const create = useCreateRule()
  const [ten, setTen] = useState('')
  const [trigger, setTrigger] = useState('contact_created')
  const [moChon, setMoChon] = useState(false)
  const tm = triggerMeta(trigger)
  const tao = () => create.mutate(
    { name: ten.trim(), trigger, enabled: false, flowVersion: 2, flowConfig: emptyFlowConfig(trigger) },
    {
      onSuccess: (r) => {
        onboardingReturn.complete(4, { autoReturn: false })
        nav(onboardingReturn.carry(`/automation/flow/${r.id}`, 4))
      },
      onError: (e) => toast.error(apiError(e)),
    },
  )
  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{t('list.createFlow')}</DialogTitle><DialogDescription>{t('createDialog.description')}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <button type="button" onClick={onAi} className="flex w-full items-center gap-3 rounded-lg bg-primary px-4 py-3 text-left text-primary-foreground shadow-md transition hover:bg-primary/90">
              <Bot className="h-7 w-7 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-base font-semibold">{t('list.createWithAi')}</span><span className="block text-xs opacity-90">{t('createDialog.aiHint')}</span></span>
              <Sparkles className="h-5 w-5 shrink-0 opacity-80" />
            </button>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><span className="h-px flex-1 bg-border" />{t('createDialog.orManual')}<span className="h-px flex-1 bg-border" /></div>
            <div><Label className="text-xs">{t('createDialog.nameLabel')}</Label><Input autoFocus value={ten} onChange={(e) => setTen(e.target.value)} placeholder={t('createDialog.namePlaceholder')} /></div>
            <div><Label className="text-xs">{t('createDialog.triggerLabel')}</Label>
              <button type="button" onClick={() => setMoChon(true)} className="flex w-full items-center gap-2 rounded-md border p-2 text-left hover:bg-accent/50">
                <VongIcon icon={tm.icon} mau={tm.mau} size="h-8 w-8" />
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{tm.ten}</span><span className="block truncate text-[11px] text-muted-foreground">{tm.moTa}</span></span>
                <span className="text-xs text-primary">{t('createDialog.change')}</span>
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-1">
              <Button variant="ghost" onClick={onChonMau}><Sparkles className="h-4 w-4" /> {t('list.chooseTemplate')}</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{t('common:actions.cancel')}</Button>
              <Button disabled={!ten.trim() || create.isPending} onClick={tao}>{t('createDialog.createOpen')}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ChonKichHoatDialog open={moChon} hienTai={trigger} onClose={() => setMoChon(false)} onChon={setTrigger} />
    </>
  )
}
